import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { fetchCategory, fetchFacts } from "@/lib/api";
import {
  getCategoryProgress,
  markLearned,
  markShowLater,
  resetCategoryProgress,
} from "@/lib/progress";
import Header from "@/components/Header";
import ProgressBar from "@/components/ProgressBar";
import FactCard from "@/components/FactCard";
import KeyHint from "@/components/KeyHint";
import { Check, RotateCcw, Shuffle, ChevronLeft, ChevronRight, RefreshCcw, Home as HomeIcon } from "lucide-react";

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Study() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shuffle, setShuffle] = useState(false);
  const [queue, setQueue] = useState([]); // current ordering of fact ids to study (excludes already learned)
  const [cursor, setCursor] = useState(0);
  const [progress, setProgress] = useState({ learned: [], showLater: [] });

  // Build the queue from facts + progress
  const buildQueue = useCallback(
    (allFacts, prog, doShuffle) => {
      const learnedSet = new Set(prog.learned);
      let remaining = allFacts.filter((f) => !learnedSet.has(f.id));
      if (doShuffle) {
        remaining = shuffleArray(remaining);
      } else {
        // Put "showLater" items at the end while keeping their relative order
        const laterSet = new Set(prog.showLater);
        const normal = remaining.filter((f) => !laterSet.has(f.id));
        const later = remaining.filter((f) => laterSet.has(f.id));
        remaining = [...normal, ...later];
      }
      return remaining.map((f) => f.id);
    },
    []
  );

  useEffect(() => {
    (async () => {
      try {
        const [cat, fs] = await Promise.all([
          fetchCategory(categoryId),
          fetchFacts(categoryId, { onlyTrue: true }),
        ]);
        setCategory(cat);
        setFacts(fs);
        const prog = getCategoryProgress(categoryId);
        setProgress(prog);
        setQueue(buildQueue(fs, prog, false));
        setCursor(0);
      } catch (e) {
        setError("İçerik yüklenemedi.");
      } finally {
        setLoading(false);
      }
    })();
  }, [categoryId, buildQueue]);

  const factsById = useMemo(() => {
    const m = {};
    facts.forEach((f) => (m[f.id] = f));
    return m;
  }, [facts]);

  const currentFact = queue[cursor] ? factsById[queue[cursor]] : null;
  const total = facts.length;
  const learnedCount = progress.learned.length;
  const isComplete = !loading && total > 0 && learnedCount >= total;

  // Redirect to completion screen
  useEffect(() => {
    if (isComplete) {
      navigate(`/study/${categoryId}/complete`, { replace: true });
    }
  }, [isComplete, categoryId, navigate]);

  const goNext = useCallback(() => {
    setCursor((c) => Math.min(c + 1, Math.max(0, queue.length - 1)));
  }, [queue.length]);

  const goPrev = useCallback(() => {
    setCursor((c) => Math.max(0, c - 1));
  }, []);

  const handleLearned = useCallback(() => {
    if (!currentFact) return;
    const newProg = markLearned(categoryId, currentFact.id);
    setProgress(newProg);
    // Remove current from queue and stay at same index (next item slides in)
    setQueue((q) => {
      const next = q.filter((id) => id !== currentFact.id);
      // keep cursor in bounds
      setCursor((c) => Math.min(c, Math.max(0, next.length - 1)));
      return next;
    });
  }, [categoryId, currentFact]);

  const handleShowLater = useCallback(() => {
    if (!currentFact) return;
    const newProg = markShowLater(categoryId, currentFact.id);
    setProgress(newProg);
    // Move current to end of queue
    setQueue((q) => {
      if (q.length <= 1) return q;
      const id = currentFact.id;
      const without = q.filter((x) => x !== id);
      return [...without, id];
    });
    // cursor stays; new item slides in
  }, [categoryId, currentFact]);

  const handleShuffle = useCallback(() => {
    const next = !shuffle;
    setShuffle(next);
    setQueue(buildQueue(facts, progress, next));
    setCursor(0);
  }, [shuffle, facts, progress, buildQueue]);

  const handleReset = useCallback(() => {
    if (!window.confirm("Bu kategori için ilerlemeyi sıfırlamak istediğinize emin misiniz?")) return;
    resetCategoryProgress(categoryId);
    const fresh = { learned: [], showLater: [] };
    setProgress(fresh);
    setQueue(buildQueue(facts, fresh, shuffle));
    setCursor(0);
  }, [categoryId, facts, shuffle, buildQueue]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.code === "Space") {
        e.preventDefault();
        handleLearned();
      } else if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        handleShowLater();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        handleShuffle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleLearned, handleShowLater, goNext, goPrev, handleShuffle]);

  if (loading) {
    return (
      <div className="min-h-screen bg-paper bg-grain">
        <Header />
        <div className="max-w-3xl mx-auto px-4 mt-24" data-testid="study-loading">
          <div className="h-[44vh] rounded-[28px] bg-white/70 border border-[#e8eae3] animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-paper bg-grain">
        <Header />
        <div className="max-w-3xl mx-auto px-4 mt-24 text-center" data-testid="study-error">
          <p className="text-[#5c635e] mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex h-12 items-center gap-2 px-6 rounded-full bg-[#2e4733] text-[#f5f6f2]"
          >
            <HomeIcon size={14} /> Ana sayfa
          </Link>
        </div>
      </div>
    );
  }

  if (!currentFact && total === 0) {
    return (
      <div className="min-h-screen bg-paper bg-grain">
        <Header />
        <div className="max-w-3xl mx-auto px-4 mt-24 text-center" data-testid="empty-category">
          <p className="text-[#5c635e] mb-6">Bu kategoride henüz bilgi yok.</p>
          <Link to="/" className="inline-flex h-12 items-center gap-2 px-6 rounded-full bg-[#2e4733] text-[#f5f6f2]">
            <HomeIcon size={14} /> Ana sayfa
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper bg-grain relative pb-20">
      <Header
        right={
          <div className="hidden md:flex items-center gap-2 label-eyebrow text-[#5c635e]">
            <span className="font-serif-fact normal-case tracking-normal text-[15px] text-[#1c2723]">
              {category?.name}
            </span>
          </div>
        }
      />

      <main className="relative z-10 max-w-5xl mx-auto px-4 md:px-2 pt-10 md:pt-14">
        <div className="mb-10" data-testid="study-progress">
          <ProgressBar value={learnedCount} total={total} />
        </div>

        {/* Mobile category title */}
        <div className="md:hidden text-center mb-6">
          <span className="font-serif-fact text-lg text-[#1c2723]">{category?.name}</span>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-6 max-w-4xl mx-auto">
          <div className="flex items-center gap-2">
            <button
              data-testid="btn-prev"
              onClick={goPrev}
              className="h-10 w-10 rounded-full border border-[#e8eae3] flex items-center justify-center text-[#5c635e] hover:text-[#1c2723] hover:bg-white transition-colors disabled:opacity-30"
              disabled={cursor === 0}
              aria-label="Önceki"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              data-testid="btn-next"
              onClick={goNext}
              className="h-10 w-10 rounded-full border border-[#e8eae3] flex items-center justify-center text-[#5c635e] hover:text-[#1c2723] hover:bg-white transition-colors disabled:opacity-30"
              disabled={cursor >= queue.length - 1}
              aria-label="Sonraki"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              data-testid="btn-shuffle"
              onClick={handleShuffle}
              className={`flex items-center gap-2 h-10 px-4 rounded-full border transition-colors text-sm ${
                shuffle
                  ? "bg-[#2e4733] text-[#f5f6f2] border-[#2e4733]"
                  : "border-[#e8eae3] text-[#5c635e] hover:text-[#1c2723] hover:bg-white"
              }`}
            >
              <Shuffle size={14} />
              <span className="hidden sm:inline">{shuffle ? "Karışık" : "Karıştır"}</span>
            </button>
            <button
              data-testid="btn-reset"
              onClick={handleReset}
              className="flex items-center gap-2 h-10 px-4 rounded-full border border-[#e8eae3] text-[#5c635e] hover:text-[#1c2723] hover:bg-white transition-colors text-sm"
            >
              <RefreshCcw size={14} />
              <span className="hidden sm:inline">Sıfırla</span>
            </button>
          </div>
        </div>

        {/* Card */}
        {currentFact ? (
          <FactCard fact={currentFact} index={cursor} total={queue.length} />
        ) : (
          <div className="text-center py-20 text-[#5c635e]" data-testid="all-cleared">
            Tüm bilgileri öğrendiniz! Devam etmek için sıfırlayın.
          </div>
        )}

        {/* Actions */}
        <div className="mt-10 flex flex-col-reverse sm:flex-row items-center justify-center gap-3 sm:gap-4 max-w-2xl mx-auto">
          <button
            data-testid="btn-show-later"
            onClick={handleShowLater}
            disabled={!currentFact}
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-[#e8eae3] text-[#1c2723] hover:bg-[#d6d9ce] active:scale-[0.98] transition-all duration-200 disabled:opacity-40"
          >
            <RotateCcw size={16} strokeWidth={2.2} className="group-hover:-rotate-45 transition-transform" />
            <span className="font-medium">Sonra göster</span>
            <KeyHint keyLabel="R" />
          </button>

          <button
            data-testid="btn-learned"
            onClick={handleLearned}
            disabled={!currentFact}
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 h-14 px-9 rounded-full bg-[#2e4733] text-[#f5f6f2] hover:bg-[#1c2723] active:scale-[0.98] transition-all duration-200 disabled:opacity-40 shadow-[0_8px_24px_-8px_rgba(46,71,51,0.45)]"
          >
            <Check size={18} strokeWidth={2.4} />
            <span className="font-medium">Öğrendim</span>
            <KeyHint keyLabel="Space" variant="dark" />
          </button>
        </div>

        {/* Footer hints */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[#a3aba6]">
          <span className="flex items-center gap-2"><KeyHint keyLabel="←" /> önceki</span>
          <span className="flex items-center gap-2"><KeyHint keyLabel="→" /> sonraki</span>
          <span className="flex items-center gap-2"><KeyHint keyLabel="S" /> karıştır</span>
        </div>
      </main>
    </div>
  );
}
